import { useEffect, useMemo, useRef, useState } from 'react'
import {
  DynamicDataSheetGrid, createAddRowsComponent, createContextMenuComponent, createTextColumn, keyColumn,
} from 'react-datasheet-grid'
import type { AddRowsComponentProps, Column, ContextMenuComponentProps, ContextMenuItem, DataSheetGridRef } from 'react-datasheet-grid'
import 'react-datasheet-grid/dist/style.css'
// 그리드는 DynamicDataSheetGrid — 기본 DataSheetGrid(Static)는 처음 받은 columns·콜백을 계속 써서 행 끝 칸·점검 표시·공동 편집 표시가 안 바뀐다
import type { DeviceSpecDevice, DeviceSpecTag } from '../../types'
import type { ChannelDef } from './catalog'
import type { SpecIssues, TagField } from './model'
import { seriesValue } from './fillSeries'
import { focasSuggest } from './focas'
import { isFormula } from './formula'
import { FORMULA_FIELDS, cellSource, recomputeDevice, setCellInput } from './recompute'
import { hasAddress } from './useSpecDraft'
import type { FormulaField } from './recompute'
import FormulaCell from './FormulaCell'
import ChoiceCell from './ChoiceCell'
import type { ChoiceData } from './ChoiceCell'
import { releaseGrid, useF2Edit } from './useF2Edit'
import { useFillHandle } from './useFillHandle'
import type { SheetCell } from './useSheetTabs'
import { useStartCell } from './useStartCell'
import type { Sel } from './useFillHandle'
import type { FormulaCellData } from './FormulaCell'
import type { FormulaContext } from './formula'
import { DATA_TYPES } from './catalog'
import { emptyTag, newKey, normalizeAddress, normalizeDataType } from './model'
import { markClass } from './presenceMarks'
import type { CellMarks } from './presenceMarks'
import { useLocate } from './useLocate'
import type { CellNote } from './SheetStatus'

// 디바이스 태그 표 — 엑셀처럼: 방향키 이동, 클릭 후 바로 입력, 드래그 범위 선택, 복사·잘라내기·붙여넣기(엑셀과 서로),
// Delete 로 범위 지우기, 우클릭으로 행 삽입·복제·삭제. (react-datasheet-grid)
// 채우기 핸들(useFillHandle): 그냥 끌면 똑같이 복사, Ctrl 을 누른 채 끌면 자동 증가 (fillSeries).
// freeRows(편집 모드)면 표 아래에 빈 행을 늘 깔아 두어 [추가] 없이 바로 입력한다 — 입력한 행만 태그가 되고,
// 끝쪽의 손대지 않은 빈 행은 저장하지 않는다 (중간에 비운 행은 엑셀처럼 남긴다).
// '=' 로 시작하면 칸을 벗어날 때 계산해 값으로 바꾼다 (formula.ts). 그래서 입력 중에는 값을 올리지 않는다(continuousUpdates false).
// 주소는 입력·붙여넣기 때 LiveEdge 형식으로 맞춘다 (Modbus "1, 3x0" → "1-3x0").

const ROW_HEIGHT = 32

const AddRowsBase = createAddRowsComponent({ button: '추가', unit: '행' })
// React 19 타입(FC → ReactNode 반환)과 그리드 prop 타입(ReactElement 반환)을 맞추는 고정 래퍼
const AddRows = (props: AddRowsComponentProps) => <AddRowsBase {...props} />

const MENU_LABELS: Record<ContextMenuItem['type'], string> = {
  INSERT_ROW_BELLOW: '아래에 행 삽입',
  DUPLICATE_ROW: '행 복제',
  DELETE_ROW: '행 삭제',
  DUPLICATE_ROWS: '선택한 행 복제',
  DELETE_ROWS: '선택한 행 삭제',
  COPY: '복사',
  CUT: '잘라내기',
  PASTE: '붙여넣기',
}

const ContextMenuBase = createContextMenuComponent(item => (
  <>{MENU_LABELS[item.type]}{'fromRow' in item && <span className="text-gray-400 ml-1">({item.fromRow + 1}~{item.toRow + 1})</span>}</>
))
const ContextMenu = (props: ContextMenuComponentProps) => <ContextMenuBase {...props} />

// 빈 칸은 null 이 아니라 '' — 명세 값은 늘 문자열
// 수식('=…')은 손대지 않고 넘긴다 — 계산은 handleChange 에서 행 문맥과 함께
const text = (parse: (v: string) => string = v => v) => {
  const keepFormula = (v: string) => (isFormula(v) ? v.trim() : parse(v))
  return createTextColumn<string>({
    deletedValue: '', continuousUpdates: false, parseUserInput: keepFormula, parsePastedValue: v => keepFormula(v.trim()),
  })
}

// 수식 칸 — 글자 칸 + '=' 자동완성 (FormulaCell). 수식('=…')은 손대지 않고 넘기고, 계산은 handleChange 에서
// 행 전체를 받는 칸 — 값(tag[칸])을 보여 주고, 편집·복사·붙여넣기는 수식 원문(tag.formulas[칸]) 기준
const formulaColumn = (field: FormulaField, parse: (v: string) => string, device: FormulaContext['device'], addressSuggest?: FormulaCellData['addressSuggest']): Partial<Column<DeviceSpecTag, FormulaCellData, string>> => ({
  id: field,
  component: FormulaCell,
  columnData: { field, parse, device, addressSuggest },
  deleteValue: ({ rowData }) => setCellInput(rowData, field, '', parse),
  copyValue: ({ rowData }) => cellSource(rowData, field),
  pasteValue: ({ rowData, value }) => setCellInput(rowData, field, value.replace(/[\n\r]+/g, ' '), parse),
  isCellEmpty: ({ rowData }) => !cellSource(rowData, field),
})


const dataTypeColumn: Partial<Column<string, ChoiceData, string>> = {
  component: ChoiceCell, // 직접 그린 목록
  columnData: { options: DATA_TYPES.map(t => ({ value: t, label: t })), fallback: 1 }, // 처음 열면 Word
  keepFocus: true,
  disableKeys: true,
  deleteValue: () => 'Word',
  copyValue: ({ rowData }) => rowData,
  pasteValue: ({ value }) => normalizeDataType(value),
}

// 행마다 값이 달라지는 수식 — ROW(행 번호)·↑/UP(위 칸)
const ROW_RELATIVE = /\bROW\b|↑|\bUP\b/i

const FIELD_LABELS: Record<string, string> = { address: '주소', size: '크기', dataType: '타입', arithmetic: '연산', description: '설명', alias: 'Alias' }

// 열 순서 = 그리드 선택 영역의 col 번호 (항목명은 시트에서 뺐다 — 데이터에는 남아 있지만 쓰지 않는다).
// Modbus 는 타입을 건드리지 않아(기본 Word) 타입 열을 숨긴다 — PLC 류·프로토콜 미정일 때만 보인다.
// Custom·Edge Agent 태그는 주소·설명만 쓴다 (LiveEdge 태그 양식 custom) — 크기·타입 열도 숨긴다
const ALL_FIELDS = ['address', 'size', 'dataType', 'arithmetic', 'description', 'alias'] as const
type GridField = typeof ALL_FIELDS[number]
const fieldsFor = (def: ChannelDef | undefined): readonly GridField[] =>
  def?.address === 'modbus' ? ALL_FIELDS.filter(f => f !== 'dataType')
    : def?.address === 'custom' ? ALL_FIELDS.filter(f => f !== 'dataType' && f !== 'size')
      : ALL_FIELDS

// 빈 행 (freeRows) — 키로 구분하고, 입력되면 새 키·기본값(크기 1, Word)으로 진짜 태그가 된다
const PAD = 'pad-'
const padTag = (i: number): DeviceSpecTag => ({
  key: `${PAD}${i}`, name: '', address: '', size: '', dataType: '', arithmetic: '', description: '', alias: '',
})
const isBlank = (t: DeviceSpecTag) =>
  !t.address.trim() && !t.arithmetic.trim() && !t.description.trim() && !t.alias.trim() && !Object.keys(t.formulas ?? {}).length

export type { Sel } from './useFillHandle'

/** 바깥(시트·편집 모드 툴바)에서 표를 다루는 손잡이 */
export interface TagGridHandle {
  /** 지금 선택 영역 (없으면 null) */
  getSelection: () => Sel | null
  /** 선택 영역의 열 번호 → 칸 이름 (프로토콜마다 열 구성이 다르다) */
  fieldAt: (col: number) => string | undefined
  /** 행 범위를 선택한다 (열은 지금 선택 열, 없으면 첫 열) */
  selectRows: (min: number, max: number) => void
  /** 그 행·칸으로 커서를 옮기고 화면에 보이게 스크롤 (검색 결과 이동) */
  focusField: (row: number, field: string) => void
  /** 표에서 커서를 뺀다 — 표가 문서 전체의 키 입력을 가져가므로, 팝업 입력칸(찾기 등)에 글자가 가게 하려면 먼저 놓아야 한다.
   *  커서·선택을 돌려놓는 함수를 준다 */
  release: () => () => void
}

export type TagMarks = Record<string, Partial<Record<'address' | 'alias', true>>>

export default function TagGrid({ tags, def, device, issues, showIssues, marks, remote, height, freeRows, startCell, onChange, onLocate, onNote, handleRef, onNotice }: {
  tags: DeviceSpecTag[]
  def: ChannelDef | undefined
  device: DeviceSpecDevice // 수식 계산 (설비구분·디바이스 코드 등)
  issues: SpecIssues
  showIssues: boolean
  marks?: TagMarks // 중복 표시 (편집 모드)
  remote?: CellMarks // 다른 사람이 있는 칸 (태그 키 → 칸 → 색)
  onLocate?: (tagKey: string | null, field: string | null, editing: boolean) => void // 내 커서 위치·입력 중 (공동 편집 표시)
  height?: number // 주면 그 높이 안에서 표가 스크롤 (편집 모드), 없으면 행 수만큼 늘어난다
  freeRows?: boolean // 표 아래 빈 행에 바로 입력 (편집 모드)
  startCell?: SheetCell | null // 처음 놓을 커서 자리 (단축키로 탭을 옮겼을 때 — 이전 시트와 같은 행·열)
  onChange: (tags: DeviceSpecTag[]) => void
  onNote?: (note: CellNote | null) => void // 지금 칸이 붉은·노란 이유 (상태 줄)
  handleRef?: (handle: TagGridHandle | null) => void
  onNotice?: (message: string) => void
}) {
  const gridRef = useRef<DataSheetGridRef>(null)
  const tagsRef = useRef(tags)
  const defRef = useRef(def)
  const handleRefRef = useRef(handleRef)
  useEffect(() => { tagsRef.current = tags; defRef.current = def; handleRefRef.current = handleRef })

  // 마운트 동안 한 번 등록 (handleRef 는 렌더마다 새로 만들어져도 같은 디바이스를 가리킨다)
  useEffect(() => {
    const register = handleRefRef.current
    register?.({
      getSelection: () => {
        const grid = gridRef.current
        const sel = grid?.selection ?? (grid?.activeCell ? { min: grid.activeCell, max: grid.activeCell } : null)
        return sel ? { min: { col: sel.min.col, row: sel.min.row }, max: { col: sel.max.col, row: sel.max.row } } : null
      },
      fieldAt: col => fieldsFor(defRef.current)[col],
      selectRows: (min, max) => {
        const grid = gridRef.current
        if (!grid) return
        const cur = grid.selection ?? (grid.activeCell ? { min: grid.activeCell, max: grid.activeCell } : null)
        const [c0, c1] = cur ? [cur.min.col, cur.max.col] : [0, 0]
        requestAnimationFrame(() => grid.setSelection({ min: { col: c0, row: min }, max: { col: c1, row: max } }))
      },
      focusField: (row, field) => {
        const grid = gridRef.current
        if (!grid) return
        const col = Math.max(0, (fieldsFor(defRef.current) as readonly string[]).indexOf(field))
        grid.setActiveCell({ col, row })
        // 활성 칸 표시가 새 자리에 그려진 뒤 스크롤 (펼친 직후면 표가 막 그려지는 중이라 조금 기다린다)
        setTimeout(() => wrapRef.current?.querySelector('.dsg-active-cell')?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60)
      },
      release: () => releaseGrid(gridRef),
    })
    return () => register?.(null)
  }, [])

  useF2Edit(gridRef)

  // ── 채우기 핸들 (useFillHandle) — 무엇을 어떻게 채울지만 여기서 ──
  const wrapRef = useRef<HTMLDivElement>(null)
  const locateCell = useLocate(wrapRef, onLocate)
  const fill = useFillHandle({
    wrapRef, rowHeight: ROW_HEIGHT,
    activeCell: () => gridRef.current?.activeCell ?? null,
    increments: sel => incrementsFor(sel),
    onFill: (sel, target, ctrl) => fillDown(sel, target, ctrl),
  })

  // Ctrl 일 때 이 선택이 자동 증가로 채워지는지 (수식 칸은 수식을 이어 가므로 증가로 본다)
  const incrementsFor = (sel: Sel) => (ctrl: boolean) => {
    if (!ctrl) return false
    const rows = tagsRef.current.slice(sel.min.row, sel.max.row + 1)
    const fields = fieldsFor(def)
    for (let col = sel.min.col; col <= sel.max.col; col++) {
      const field = fields[col]
      if (!field || field === 'dataType') continue
      if ((FORMULA_FIELDS as readonly string[]).includes(field) && rows.some(t => t.formulas?.[field as FormulaField])) return true
      if (seriesValue(rows.map(t => t[field] ?? ''), 1, true) != null) return true
    }
    return false
  }

  // 원본 행(sel)을 target 행까지 — Ctrl 이면 자동 증가, 아니면 똑같이 복사. 타입(드롭다운)은 늘 복사.
  // 원본이 수식이면 Ctrl 일 때 수식을 이어 가고(ROW·↑ 가 행마다 달라 값이 이어진다 — 없으면 수식 속 숫자를 늘린다),
  // 아니면 보이는 값을 복사한다
  const fillDown = (sel: Sel, target: number, ctrl: boolean) => {
    const base = tagsRef.current
    const out = [...base]
    const fields = fieldsFor(def)
    while (out.length <= target) out.push(emptyTag())
    for (let col = sel.min.col; col <= sel.max.col; col++) {
      const field = fields[col]
      if (!field) continue
      const rows = base.slice(sel.min.row, sel.max.row + 1)
      const formulaField = (FORMULA_FIELDS as readonly string[]).includes(field) ? field as FormulaField : null
      const formulas = formulaField ? rows.map(t => t.formulas?.[formulaField]) : []
      const src = rows.map(t => t[field] ?? '')
      // 행마다 달라지는 수식(ROW·↑)은 그대로 이어 가고, 아니면 수식 속 숫자를 늘린다 (={code}_1 → _2, _3 …)
      const formulaSeries = ctrl && formulas.length > 0 && formulas.every(f => f && !ROW_RELATIVE.test(f))
      for (let row = sel.max.row + 1; row <= target; row++) {
        const k = row - sel.max.row
        const f = ctrl ? (formulaSeries ? seriesValue(formulas as string[], k, true) : null) ?? formulas[(k - 1) % rows.length] : undefined
        if (formulaField && f) {
          out[row] = { ...out[row], formulas: { ...out[row].formulas, [formulaField]: f } }
          continue
        }
        const series = field === 'dataType' ? null : seriesValue(src, k, ctrl)
        out[row] = { ...out[row], [field]: series ?? src[(k - 1) % src.length] }
        if (formulaField && out[row].formulas?.[formulaField]) {
          const rest = { ...out[row].formulas }
          delete rest[formulaField]
          out[row] = { ...out[row], formulas: rest }
        }
      }
    }
    handleChange(out, [{ type: 'UPDATE', fromRowIndex: sel.max.row + 1, toRowIndex: target + 1 }])
    // 채운 범위를 선택해 둔다 (늘어난 행이 그려진 뒤)
    requestAnimationFrame(() => gridRef.current?.setSelection({
      min: { col: sel.min.col, row: sel.min.row }, max: { col: sel.max.col, row: target },
    }))
  }

  const handleChange = (next: DeviceSpecTag[], operations: { type: string; fromRowIndex: number; toRowIndex: number }[]) => {
    // 빈 행 정리 — 마지막으로 내용이 있는 행까지만 태그로, 그 사이의 빈 행은 진짜 행으로 바꾼다.
    // 끝쪽의 빈 태그(내용을 다른 행으로 옮겨 비게 된 행 등)도 같이 걷어 낸다 — 아래 빈 행과 똑같이 보이니까
    if (next.some(t => t.key.startsWith(PAD))) {
      let last = -1
      next.forEach((t, i) => { if (!isBlank(t)) last = i })
      next = next.slice(0, last + 1).map(t => (t.key.startsWith(PAD)
        ? { ...t, key: newKey(), size: t.size || '1', dataType: t.dataType || 'Word' }
        : t))
    }
    // 수식 계산은 명세서 상태(useSpecDraft)가 한다 — 여기서는 방금 바뀐 행에 계산 안 되는 수식이 있으면 알려 준다
    onChange(next)
    const errors = recomputeDevice({ ...device, tags: next }).errors
      .filter(e => operations.some(o => e.row >= o.fromRowIndex && e.row < o.toRowIndex))
      .map(e => `${e.row + 1}행 ${FIELD_LABELS[e.field]}: ${e.message}`)
    if (errors.length > 0) onNotice?.(`수식을 계산하지 못했습니다: ${errors[0]}${errors.length > 1 ? ` 외 ${errors.length - 1}건` : ''}`)
  }

  const display = useMemo(() => {
    if (!freeRows) return tags
    const visible = Math.ceil((height ?? 0) / ROW_HEIGHT)
    const pads = Math.max(visible - tags.length, 0) + 30
    return [...tags, ...Array.from({ length: pads }, (_, j) => padTag(tags.length + j))]
  }, [tags, freeRows, height])

  // 계산 안 되는 수식 칸 — "키:칸" → 이유
  const formulaErrors = useMemo(() => new Map(recomputeDevice({ ...device, tags }).errors.map(e => [`${e.key}:${e.field}`, e.message])), [device, tags])

  // 지금 칸이 붉은·노란 이유 — 칸 표시(invalid)와 같은 순서로: 수식 오류 → 점검 → 겹침
  const [activeCell, setActiveCell] = useState<{ col: number; row: number } | null>(null)
  const note = useMemo<CellNote | null>(() => {
    const tag = activeCell ? display[activeCell.row] : undefined
    const field = activeCell ? fieldsFor(def)[activeCell.col] : undefined
    if (!tag || !field || tag.key.startsWith(PAD)) return null
    const at = `${activeCell!.row + 1}행 ${FIELD_LABELS[field]}`
    const formula = formulaErrors.get(`${tag.key}:${field}`)
    if (formula) return { tone: 'error', text: `${at} — 수식을 계산하지 못했습니다: ${formula}` }
    const issue = showIssues ? issues.tag[tag.key]?.[field as TagField] : undefined
    if (issue) return { tone: 'error', text: `${at} — ${issue}` }
    if ((field === 'address' || field === 'alias') && marks?.[tag.key]?.[field])
      return { tone: 'warn', text: field === 'address' ? `${at} — 이 디바이스에 같은 주소가 또 있습니다` : `${at} — 다른 태그와 Alias 가 겹칩니다 (명세서 전체)` }
    return null
  }, [activeCell, display, def, formulaErrors, showIssues, issues, marks])
  const onNoteRef = useRef(onNote)
  useEffect(() => { onNoteRef.current = onNote })
  useEffect(() => { onNoteRef.current?.(note) }, [note])
  useEffect(() => () => onNoteRef.current?.(null), [])

  const columns = useMemo<Column<DeviceSpecTag>[]>(() => {
    // 계산 못 한 수식·(전송 전 점검을 켜면) 빠진·틀린 칸은 붉게, (중복 표시를 켜면) 겹치는 주소·Alias 는 노랗게.
    // 수식이 든 칸은 모서리 표시 (dsg-cell-formula)
    const invalid = (field: TagField | 'description' | 'alias' | 'arithmetic') => ({ rowData }: { rowData: DeviceSpecTag }) => {
      const formula = !!rowData.formulas?.[field as FormulaField]
      const tone = formulaErrors.has(`${rowData.key}:${field}`) || (showIssues && field in (issues.tag[rowData.key] ?? {}))
        ? 'dsg-cell-invalid'
        : (field === 'address' || field === 'alias') && marks?.[rowData.key]?.[field] ? 'dsg-cell-dup' : ''
      return [tone, formula ? 'dsg-cell-formula' : '', markClass(remote, rowData.key, field)].filter(Boolean).join(' ') || undefined
    }
    return [
      {
        // FANUC 는 주소 자동완성 (함수·인자·필드, 자주 쓰는 주소)
        ...formulaColumn('address', v => normalizeAddress(def, v), device, def?.address === 'focas' ? focasSuggest : undefined),
        title: <span title={def?.addressHint}>주소</span>, basis: 170, grow: 2, minWidth: 120,
        cellClassName: invalid('address'),
      },
      ...(fieldsFor(def).includes('size')
        ? [{ ...formulaColumn('size', v => v, device), title: '크기', basis: 60, grow: 0, minWidth: 56, cellClassName: invalid('size') }]
        : []),
      ...(fieldsFor(def).includes('dataType')
        ? [{ ...keyColumn<DeviceSpecTag, 'dataType'>('dataType', dataTypeColumn), title: '타입', basis: 110, grow: 0, minWidth: 100, cellClassName: invalid('dataType') }]
        : []),
      { ...keyColumn<DeviceSpecTag, 'arithmetic'>('arithmetic', text()), title: '연산', basis: 100, grow: 1, minWidth: 80, cellClassName: invalid('arithmetic') },
      { ...formulaColumn('description', v => v, device), title: '설명', basis: 160, grow: 2, minWidth: 100, cellClassName: invalid('description') },
      { ...formulaColumn('alias', v => v, device), title: 'Alias', basis: 150, grow: 1, minWidth: 110, cellClassName: invalid('alias') },
    ]
  }, [def, device, issues, showIssues, marks, remote, formulaErrors])
  useStartCell(gridRef, startCell, columns.length, display.length)

  return (
    <div ref={wrapRef} className="spec-grid relative">
      {/* 프로토콜(주소 형식)이 바뀌면 열 구성이 달라진다 — 커서·선택이 없어진 열을 가리키지 않게 새로 만든다 */}
      <DynamicDataSheetGrid<DeviceSpecTag>
        key={def?.address ?? 'none'}
        ref={gridRef}
        onActiveCellChange={({ cell }) => {
          fill.onActiveCell(cell, !!gridRef.current?.selection)
          setActiveCell(cell ? { col: cell.col, row: cell.row } : null)
          const key = cell ? display[cell.row]?.key : undefined
          locateCell(key && !key.startsWith(PAD) ? key : null, cell ? fieldsFor(def)[cell.col] ?? null : null)
        }}
        onSelectionChange={({ selection: next }) => fill.onSelection(next)}
        disableExpandSelection
        // 주소가 없는 태그는 저장되지 않는다 — 흐리게 보인다
        rowClassName={({ rowData }) => (!rowData.key.startsWith(PAD) && !hasAddress(rowData) ? 'tag-no-address' : undefined)}
        value={display}
        onChange={handleChange}
        columns={columns}
        rowKey="key"
        createRow={emptyTag}
        duplicateRow={({ rowData }) => ({ ...rowData, key: newKey() })}
        rowHeight={ROW_HEIGHT}
        headerRowHeight={ROW_HEIGHT}
        // 일반 화면은 표 안에서 따로 스크롤하지 않고 페이지가 스크롤된다 (디바이스당 태그 수십 개 규모)
        height={height ?? ROW_HEIGHT * (Math.max(tags.length, 1) + 1) + 2}
        onScroll={fill.place}
        // 빈 칸에서 Delete 를 누르면 행 전체 선택 → 한 번 더 누르면 행 삭제되는 "스마트 삭제"는 끈다 (엑셀과 다르고 실수로 행이 지워진다).
        // 행 삭제는 우클릭 메뉴로
        disableSmartDelete
        addRowsComponent={freeRows ? false : AddRows}
        contextMenuComponent={ContextMenu}
      />
      {fill.element}
    </div>
  )
}
